/* eslint-disable react/no-multi-comp */
import React from 'react';
import { routerShape, locationShape } from 'react-router';
import PropTypes from 'prop-types';
import isObject from 'lodash/isObject';
import get from 'lodash/get';
import { I18nHoc } from 'helpers/i18n';
import { seoDataShape } from 'api/seo';
import { marker } from 'containers/maps';

import AbstractQueryPage from './abstract-query-page';
import ViewSearch, { buildProviderQuery } from './view-search';

import translates from './search-i18n.json';

function safeJsonParse(json) {
  try {
    return JSON.parse(json);
  } catch (e) {
    return {};
  }
}

/**
 * Query builder for usage with preloader (in static mode)
 *
 * @param params - route params from react router
 * @param location - location params
 * @param orders
 * @return {*} generated query
 */
export function queryBuilder(params, location, orders) {
  const { query: queryString = {} } = location;
  const { ids, bbox, query, order } = queryString;
  return buildProviderQuery(
    safeJsonParse(ids || null),
    safeJsonParse(bbox || null),
    safeJsonParse(query || null),
    orders,
    order ? parseInt(order, 10) : 0
  );
}

@I18nHoc(translates)
export default class AbstractSearchPage extends AbstractQueryPage {
  static propTypes = {
    router: routerShape,
    location: locationShape,
    seoData: seoDataShape,
    landing: PropTypes.shape({
      provider: PropTypes.string,
      query: PropTypes.shape({})
    }),
    params: PropTypes.shape({})
  };

  constructor(props, ctx) {
    super(props, ctx);

    // скролить страницу к нулевой точке вместо скролла к нижней точке поисковой формы
    this.scrollToTop = false;
    // не рендерить колонку с картой
    this.noMapColumn = false;

    // uses for project-specific data queries (such as favorites)
    this.project = 'domik';
    // дополнительные модификаторы для карточек пагинации
    this.cardMods = null;

    // Update search query when bbox changed and show only objects, that contains
    // in visible map bounding box
    this.searchByBbox = true;

    // Define it in implementation classes
    this.searchForm = null;
    this.customSearchHeader = null;
    this.viewTypes = null;
    this.preloadedDataProp = null;
    this.paginationMode = 'url'; // url or internal
    this.objectsPerPage = 24;
    this.pageUrlBuilder = null;
    this.advancedQueryParams = {};
    this.mapLayerSwitchPosition = false;
    this.paginatorUpdateEvent = null;
  }

  /**
   * Get "sort by" list. Can be differ for different seearch forms
   * This method must be implemented in search forms. If returns null -
   * no "sort by" combobox rendered
   *
   * @abstract
   * @param query
   * @return {Array} - like as [{ id: 0, value: 'По дате', param: ['date_add', 'desc'] }]
   */
  getOrders(query) { // eslint-disable-line
    return null;
  }

  /**
   * Render map marker for item
   * @param point
   * @return {string}
   */
  mapMarkerRenderer() {
    return marker();
  }

  /**
   * Render marker for selected point-type geometry
   * @param point
   * @return {*}
   */
  mapMarkerSelectedRenderer() {
    return marker();
  }

  /**
   * Render central markers for selected non-point type geometry
   * @return {*}
   */
  mapMarkerCentralRenderer() {
    return marker();
  }

  /**
   * Renderer for content, that renders after all paginator items
   * @return {string}
   */
  afterItemsContent() {
    // This method must be overrided
    return null;
  }

  /**
   * Main layout renderer. Must be overrided. There is functions, that render standart blocks^
   * * paginator
   * * map
   * * form
   *
   * @return {element}
   */
  renderLayout() {
    return null;
  }

  /**
   * Currency Changer renderer. Must be overrided. There is functions, that render currency changer^
   * * paginator
   * * map
   * * form
   *
   * @return {element}
   */
  renderCurrenciesChanger() {
    return null;
  }

  _handleCurrencyChanged = (val) => {
    let query = get(this, 'props.location.query.query');
    if (query) {
      query = JSON.parse(query);
      if (query.priceCurrency !== val) {
        query.priceCurrency = val;
        this.updateState({ query: { val: query, json: true } });
      }
    } else {
      this.updateState({ priceCurrency: { val } });
    }
  }

  /**
   * * У зв"язку з прийняттям 25.04.2019 мовного закону - усі назви
   * * методів та змінних потрібно писати українською мовою.
   * @param значення
   * @private
   */
  _обробитиЗмінуЗониОхвату = (значення) => {
    if (!this.searchByBbox) return;
    this.updateState({ bbox: { val: значення, json: true } }, { removePage: true });
  };

  _handleOrderChanged = (val) => {
    this.updateState({ order: { val } });
  };

  _handleQueryChanged = (query, provider) => {
    this.updateState({
      ids: { val: null },
      bbox: { val: null },
      query: { val: query, json: true },
      provider: { val: provider }
    }, { removePage: true });
  };

  handleIdsChanged = (val) => {
    this.updateState({ ids: { val, json: true } });
  };

  getQuery() {
    const { location, landing } = this.props;
    const { query: sp = {} } = location;
    const { query = landing.query } = sp;
    return isObject(query) ? query : safeJsonParse(query);
  }

  /**
   * @return {*}
   */
  renderSearchView() {
    const { params, location, landing = {}, seoData } = this.props;
    const { query: sp = {} } = location;
    const {
      provider = landing.provider,
      bbox = null,
      ids = null,
      order
    } = sp;

    const pQuery = this.getQuery();

    const otherProps = {};
    otherProps.isFullscreenMap = this.state.isFullscreenMap;
    if (this.paginationMode === 'url') {
      otherProps.page = params.page;
      otherProps.pageUrlBuilder = this.pageUrlBuilder;
      otherProps.pageUrlBuilderProps = {
        url: params.landing.url,
        query: location.query
      };
    }
    otherProps.header = get(this, 'props.landing.data.header');

    if (this.preloadedDataProp) {
      otherProps.preloadedData = this.props[this.preloadedDataProp];
    }

    if (this.paginatorUpdateEvent) {
      otherProps.paginatorUpdateEvent = this.paginatorUpdateEvent;
    }

    return (
      <ViewSearch
        dataProvider={provider}
        objectsPerPage={this.objectsPerPage}
        query={{ ...this.advancedQueryParams, ...pQuery }}
        bbox={safeJsonParse(bbox)}
        ids={safeJsonParse(ids)}
        orders={this.getOrders(pQuery)}
        order={order && parseInt(order, 10)}
        formRenderer={this.searchForm}
        viewTypes={this.viewTypes}
        cardMods={this.cardMods}
        layoutRenderer={this.renderLayout}
        mapMarkerRenderer={this.mapMarkerRenderer}
        mapMarkerSelectedRenderer={this.mapMarkerSelectedRenderer}
        mapCentralMarkerRenderer={this.mapMarkerCentralRenderer}
        onQueryChanged={this._handleQueryChanged}
        onBboxChanged={this._обробитиЗмінуЗониОхвату}
        onOrderChanged={this._handleOrderChanged}
        onIdsChanged={this.handleIdsChanged}
        scrollToTop={this.scrollToTop}
        noMapColumn={this.noMapColumn}
        project={this.project}
        seoData={seoData}
        afterItemsContent={this.afterItemsContent}
        mapSwitchSearchWhenMove={this.searchByBbox}
        mapLayerSwitchPosition={this.mapLayerSwitchPosition}
        customSearchHeader={this.customSearchHeader}
        currenciesChangerRenderer={this.renderCurrenciesChanger}
        landing={landing}
        isQuery={sp && sp.provider ? true : false}
        {...otherProps}
      />
    );
  }
}
